import { useEffect, useRef, useState } from 'react';

import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useFormikForm } from '#/common/hooks';
import { apiEndpoints, queryKeys, useGetQuery, usePostMutation } from '#/services';
import { MAX_FILE_SIZE } from '#/utils/constants';
import { getFileNameFromUrl, replaceWhiteSpaceWithUnderscore, toSnakeCase } from '#/utils/helperFunctions';

function useStickyButtonWidget() {
  const [fileFieldName, setFileFieldName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isFieldSubmit, setIsFieldSubmit] = useState(false);
  const [isResponsive, setIsResponsive] = useState(false);
  const [fileQueue, setFileQueue] = useState([]);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const urlParams = new URLSearchParams(window.location.search);
  const leadId = urlParams.get('id');

  const fileRef = useRef(null);

  const handlePreviewToggle = () => {
    resetForm();
    setIsFieldSubmit(!isFieldSubmit);
  };

  const { data: stickyBtnDetail } = useGetQuery({
    key: queryKeys.STICKY_BTN_DETAIL,
    url: `${apiEndpoints.GET_STICKY_BUTTON}${leadId}/`,
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!leadId,
    },
  });

  const onError = ({ response: { data } } = {}) => {
    const { detail, non_field_errors, image } = data || {};

    const errorMessage = image[0] || detail || non_field_errors[0] || 'An error occurred';

    toast.error(errorMessage);
    processNextFile();
  };

  const onSuccess = () => {
    setIsFieldSubmit(false);
    window.parent.postMessage({ action: 'close' }, '*');
  };

  const { mutate: onLeadSubmit, isPending: isLeadSubmitLoading } = usePostMutation({
    url: apiEndpoints.LEAD_RECORD,
    onSuccess,
    onError,
  });

  const onUploadFileSuccess = ({ data }) => {
    if (data?.file) {
      setUploadedFiles(prev => {
        const existingFiles = prev[fileFieldName] ?? [];

        return {
          ...prev,
          [fileFieldName]: [...existingFiles, data?.file],
        };
      });

      processNextFile();
    }
  };

  const { mutate: uploadFile, isPending: isUploadFileLoading } = usePostMutation({
    url: apiEndpoints.UPLOAD_FILE,
    onSuccess: onUploadFileSuccess,
    onError,
  });

  const processNextFile = () => {
    const nextIndex = currentFileIndex + 1;
    setCurrentFileIndex(nextIndex);

    if (nextIndex < fileQueue.length) {
      const { file, fieldName } = fileQueue[nextIndex];

      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(fieldName);
      uploadFile({ payload: formData });
    } else {
      setIsProcessingFiles(false);
      setFileQueue([]);
      setCurrentFileIndex(0);
    }
  };

  const handleRemoveFile = (fieldName, fileUrl) => () => {
    setUploadedFiles(prev => {
      const existingFiles = prev[fieldName];

      if (!existingFiles) return prev;

      const updatedFiles = existingFiles.filter(file => file !== fileUrl);

      if (updatedFiles.length === 0) {
        const newFiles = { ...prev };
        delete newFiles[fieldName];
        formik.setFieldValue(fieldName, '');
        return newFiles;
      }

      const currentFiles = formik.values[fieldName];

      if (currentFiles) {
        if (existingFiles && existingFiles.length <= 1) {
          formik.setFieldValue(fieldName, '');
        }
      }

      return {
        ...prev,
        [fieldName]: updatedFiles,
      };
    });
  };

  const handleFileChange = fieldName => e => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);

    const newFileQueue = [];
    let hasOversizedFile = false;
    let hasDuplicateFile = false;

    const existingFiles = uploadedFiles[fieldName]?.map(item => getFileNameFromUrl(item));

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (existingFiles?.includes(replaceWhiteSpaceWithUnderscore(file?.name))) {
        hasDuplicateFile = true;
        toast.error('Duplicate files are not allowed!');
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        hasOversizedFile = true;
        toast.error(`File ${file.name} exceeds 10MB. Please upload a smaller file.`);
        continue;
      }

      newFileQueue.push({ file, fieldName });
    }

    if ((hasOversizedFile || hasDuplicateFile) && newFileQueue.length === 0) {
      setIsFieldSubmit(!isFieldSubmit);
      return;
    }

    const updatedQueue = [...fileQueue, ...newFileQueue];
    setFileQueue(updatedQueue);
    setCurrentFileIndex(fileQueue.length);
    setIsProcessingFiles(true);

    formik.setFieldValue(fieldName, files);

    if (newFileQueue.length > 0) {
      const { file, fieldName: firstFieldName } = newFileQueue[0];
      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(firstFieldName);
      uploadFile({ payload: formData });
    }
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleTelInputChange = fieldName => e => {
    const value = e.target.value;

    if (value === '' || /^\d+$/.test(value)) {
      setFieldValue(fieldName, value);
    }
  };

  const handleSelectChange = (fieldName, value) => {
    setFieldValue(fieldName, value);
  };

  const { lead = {} } = stickyBtnDetail?.data || {};

  const generateValidationSchema = fields => {
    if (!fields || !fields.length) return Yup.object({});

    const schema = {};

    fields.forEach(field => {
      if (!field) return;

      const { label, type } = field;
      let fieldSchema;

      const fieldName = label;

      if (!fieldName) return;

      switch (type?.toLowerCase()) {
      case 'email':
        fieldSchema = Yup.string().email('Invalid email format').required('This field is required');
        break;
      case 'number':
        fieldSchema = Yup.string()
          .matches(/^\d+$/, 'Only numeric characters are allowed')
          .required('This field is required');
        break;
      case 'upload':
        fieldSchema = Yup.mixed().test('fileRequired', 'This field is required', value => {
          return value && value.length > 0;
        });
        break;
      default:
        fieldSchema = Yup.string().required('This field is required');
      }

      schema[fieldName] = fieldSchema;
    });

    return Yup.object().shape(schema);
  };

  const generateInitialValues = fields => {
    if (!fields || !fields.length) return {};

    const initialValues = {};

    fields.forEach(field => {
      if (field && field.label) {
        initialValues[field.label] = '';
      }
    });

    return initialValues;
  };

  const onLeadFormSubmit = values => {
    const payload = {
      chatbotLead: lead?.id,
      response: toSnakeCase({
        ...values,
        ...uploadedFiles,
      }),
    };
    onLeadSubmit({ payload });
    resetForm();
    setIsFieldSubmit(true);
    setUploadedFiles([]);
  };

  const formik = useFormikForm(
    generateInitialValues(lead?.fields),
    onLeadFormSubmit,
    generateValidationSchema(lead?.fields)
  );

  const { resetForm, setFieldValue } = formik;

  const windowSizeHandler = () => {
    setIsResponsive(window.innerWidth < 576);
  };

  useEffect(() => {
    window.addEventListener('resize', windowSizeHandler);
    windowSizeHandler();

    return () => window.removeEventListener('resize', windowSizeHandler);
  }, []);

  useEffect(() => {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (!isIOS) return;

    const handleTap = e => {
      const target = e.target;

      setTimeout(() => {
        const active = document.activeElement;

        if (
          active &&
          ['INPUT', 'TEXTAREA'].includes(active.tagName) &&
          !active.classList.contains('select-field__input') &&
          !target.closest('input') &&
          !target.closest('textarea')
        ) {
          active.blur();
        }
      }, 50);
    };

    document.addEventListener('touchstart', handleTap);

    return () => {
      document.removeEventListener('touchstart', handleTap);
    };
  }, []);

  return {
    stickyBtnDetail,
    isResponsive,
    isFieldSubmit,
    formik,
    handlePreviewToggle,
    handleFileChange,
    handleTelInputChange,
    isUploadFileLoading,
    handleSelectChange,
    uploadedFiles,
    isProcessingFiles,
    handleRemoveFile,
    fileRef,
    isLeadSubmitLoading,
  };
}

export default useStickyButtonWidget;
